import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import messages from './messages';
import styles from './Storyboard.module.scss';

type StoryboardFrameProps = {
  src: string;
  frame: number;
  /** width / height of a frame, so the tile holds its shape before load */
  aspect: number;
  label?: string;
  /**
   * Render the placeholder instead of the image. The viewer sets this while
   * the grid is scrolling fast, so a fling past 1,000 frames doesn't fire
   * 1,000 requests at a route rate-limited to a burst of 60 per IP.
   */
  deferLoad?: boolean;
  highlighted?: boolean;
  onClick?: () => void;
  onLoad?: () => void;
};

// Two quiet retries ride out a 429 or a cold CDN edge; after that the tile
// says so and offers a manual retry.
const AUTO_RETRIES = 2;

export const StoryboardFrame: React.FC<StoryboardFrameProps> = ({
  src,
  frame,
  aspect,
  label,
  deferLoad = false,
  highlighted = false,
  onClick,
  onLoad,
}) => {
  const { formatMessage } = useIntl();
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
  }, []);
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [src]);

  const handleError = () => {
    if (attempt < AUTO_RETRIES) {
      retryTimer.current = setTimeout(() => setAttempt((a) => a + 1), 800 * (attempt + 1));
    } else {
      setFailed(true);
    }
  };

  const retry = () => {
    setFailed(false);
    setAttempt((a) => a + 1);
  };

  // A fresh query param per attempt, so a retry isn't answered from the
  // browser's memory of the failure.
  const attemptSrc = attempt > 0 ? `${src}&_r=${attempt}` : src;

  return (
    <figure className={cn(styles.frame, { [styles.frameHighlighted]: highlighted })} data-test="storyboard-frame">
      {failed ? (
        <div className={styles.frameFailed} style={{ aspectRatio: String(aspect) }}>
          <span>{formatMessage(messages.frameFailed)}</span>
          <button type="button" className={styles.frameRetry} onClick={retry}>
            {formatMessage(messages.frameRetry)}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.frameButton}
          style={{ aspectRatio: String(aspect) }}
          onClick={onClick}
          disabled={!onClick}
          aria-label={label ?? formatMessage(messages.frameAlt, { frame })}
        >
          {!deferLoad && (
            <img
              key={attemptSrc}
              src={attemptSrc}
              alt={formatMessage(messages.frameAlt, { frame })}
              loading="lazy"
              decoding="async"
              onLoad={onLoad}
              onError={handleError}
            />
          )}
        </button>
      )}
      {label && <figcaption>{label}</figcaption>}
    </figure>
  );
};

export default StoryboardFrame;
